import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from 'context/AuthContext';
import { useServerTime } from 'hooks/useServerTime';
import { canApproveParticipants, isTeamRo, PlayerProfile } from 'pages/Dashboard/home-reads';
import { NotFound } from 'pages/NotFound';
import { FOCO_VISIVEL } from 'utils/foco';

import { useLateralidade } from '../ApreciarRealizacao/shared/useLateralidade';
import { etapasDoCiclo, rotuloFaseAtual } from '../calendario-do-ciclo';
import CabecalhoDaMinhaEquipe from '../components/CabecalhoDaMinhaEquipe';
import {
  dataCurta,
  dentroDaAlcada,
  FAIXAS,
  inicioDoCiclo,
  podeDesfazer,
  prazoDoCiclo,
  RealizacaoParaAutorizar,
} from './autorizacao-regras';
import CartaoDeAutorizacao from './CartaoDeAutorizacao';
import { useAutorizacoes } from './useAutorizacoes';
import { useInfoDoGestor } from './useInfoDoGestor';

/** Regra de acesso de antes ("Quem vê a página"): a do aviso da Home, ou dupla MAD com `ro` de equipe. */
function podeVerAPagina(userInfo: PlayerProfile | null | undefined): boolean {
  const duplas = userInfo?.mad_dupla;
  return canApproveParticipants(userInfo) || (Array.isArray(duplas) && duplas.some(dupla => isTeamRo(dupla?.ro)));
}

const TITULO_DE_SECAO = 'm-0 font-display text-base font-bold leading-[normal] text-brand-blue-dark';
const BOTAO_DE_TEXTO = `cursor-pointer border-0 bg-transparent p-0 font-bold uppercase tracking-[0.04em] text-jj-label text-brand-blue-dark ${FOCO_VISIVEL}`;

/** Autorização do número de participantes em Realização Destaque coletiva (design `Minha Equipe.dc.html`, visão participantes). */
export default function AnalisarParticipacaoEquipe() {
  const { user, userInfo, cycles } = useAuth();
  const { internalUser, isVacationManager } = useLateralidade(user);
  const { realDatetime: agora, hasTime } = useServerTime();

  // A alçada é a do gestor efetivo: na lateralidade, a do substituído (regra de antes).
  const gestor = useInfoDoGestor(isVacationManager, internalUser?._id, userInfo);

  const prazo = useMemo(() => prazoDoCiclo(cycles), [cycles]);
  const inicio = useMemo(() => inicioDoCiclo(cycles), [cycles]);
  // `undefined` enquanto o calendário carrega; `null` quando ele não tem a Apreciação Nível 3 (PAR-85).
  const periodo = useMemo(() => {
    if (!cycles) return undefined;
    return inicio !== null && prazo !== null ? { inicio, fim: prazo } : null;
  }, [cycles, inicio, prazo]);

  // Só lê quem vai ver a lista: a Rede e quem não tem alçada param antes da leitura.
  const rede = !!user?.extra?.rede;
  const podeVer = !!user && podeVerAPagina(userInfo);
  const autorizacoes = useAutorizacoes(podeVer && !rede ? internalUser?._id : undefined, user?._id, periodo);
  const [avaliadasAbertas, setAvaliadasAbertas] = useState(false);

  // A lista só aparece com a alçada conhecida (PAR-86): até lá, carrega; se a ficha do substituído falhar, erro.
  const estado = gestor.estado === 'pronto' ? autorizacoes.estado : gestor.estado;
  const tentarDeNovo = gestor.estado === 'erro' ? gestor.tentarDeNovo : autorizacoes.tentarDeNovo;

  // PAR-53: a Rede vê NotFound antes da regra de acesso, mesmo sem alçada.
  if (rede) return <NotFound />;
  if (!podeVer) return null;

  const prazoEncerrado = hasTime && prazo !== null && agora > prazo;
  const faseAtual = hasTime ? rotuloFaseAtual(etapasDoCiclo(cycles), agora) : null;
  const prazoFormatado = prazo !== null ? dataCurta(prazo) : null;

  function cartao(realizacao: RealizacaoParaAutorizar) {
    return (
      <li key={realizacao._id}>
        <CartaoDeAutorizacao
          realizacao={realizacao}
          usuario={user?._id}
          dentroDaAlcada={dentroDaAlcada(gestor.info, realizacao)}
          prazoEncerrado={prazoEncerrado}
          desfazivel={hasTime && podeDesfazer(realizacao, user?._id, agora)}
          gravando={!!autorizacoes.gravando[realizacao._id]}
          erro={autorizacoes.erros[realizacao._id] ?? null}
          onAutorizar={() => autorizacoes.decidir(realizacao._id, true)}
          onRecusar={() => autorizacoes.decidir(realizacao._id, false)}
          onDesfazer={() => autorizacoes.desfazer(realizacao._id)}
        />
      </li>
    );
  }

  return (
    <div className="mx-auto max-w-[1000px] px-4 pb-6 leading-[normal] lg:px-0">
      <CabecalhoDaMinhaEquipe faseAtual={faseAtual} />

      {/* SPEC_DEVIATION: "Voltar" em `brand-blue-dark` (o `brand-blue` do design dá 4,3:1 no fundo do app com 11px). */}
      <Link
        to="/minha-equipe"
        className={`mb-3.5 inline-flex items-center gap-[7px] font-bold uppercase tracking-[0.05em] text-jj-label text-brand-blue-dark no-underline hover:text-brand-blue-dark ${FOCO_VISIVEL}`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
          <path d="m15 18-6-6 6-6" />
        </svg>
        Voltar para Minha Equipe
      </Link>

      {isVacationManager && internalUser && (
        <p className="m-0 mb-4 rounded-jj-lg bg-brand-white px-5 py-3 text-jj-body-sm text-content shadow-jj-01">
          Você está avaliando por <strong>{internalUser.name}</strong> ({internalUser._id})
        </p>
      )}

      {/* D3: raio de 20px e padding de 24/26px do design sem degrau. */}
      <section aria-labelledby="titulo-autorizacao" className="rounded-jj-panel bg-brand-green px-[26px] py-6">
        <h2 id="titulo-autorizacao" className="m-0 mb-3 font-display text-jj-h3 font-bold leading-jj-h3 text-brand-blue-dark">
          Autorização do número de participantes
        </h2>
        <div className="flex flex-col gap-2 text-jj-body-sm leading-jj-relaxed text-content">
          <p className="m-0">
            A Realização Destaque coletiva com até 20 participantes é autorizada automaticamente. A partir de 21 participantes,
            cabe ao líder responsável autorizar a quantidade.
          </p>
          <p className="m-0">
            A realização segue normalmente por todas as etapas do fluxo de apreciação. Contudo, se a quantidade não for
            autorizada até <b>{prazoFormatado ?? 'o último dia da Apreciação Nível 3'}</b>
            {prazoFormatado && ', último dia da Apreciação Nível 3'}, a pontuação obtida não será creditada aos participantes.
          </p>
        </div>
        <ul className="m-0 mt-4 grid list-none grid-cols-1 gap-2.5 p-0 sm:grid-cols-2 lg:grid-cols-4">
          {FAIXAS.map(faixa => (
            <li key={faixa.rotulo} className="rounded-[10px] bg-brand-white px-3.5 py-3">
              <div className="font-display text-jj-body-sm font-bold text-brand-blue-dark">{faixa.rotulo}</div>
              <div className="mt-1 text-jj-label leading-jj-body-sm text-content">{faixa.alcada}</div>
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="titulo-aguardando" className="mt-4 flex flex-col gap-3 rounded-jj-lg bg-brand-white px-6 py-[22px] shadow-jj-01">
        <div className="flex flex-wrap items-center gap-[11px]">
          <h2 id="titulo-aguardando" className={TITULO_DE_SECAO}>
            Realizações aguardando sua autorização
          </h2>
          {estado === 'pronto' && (
            // SPEC_DEVIATION: fundo `brand-blue-dark` (amarelo de 11px sobre `brand-blue` dá 4,44:1, PAR-43).
            <span className="rounded-jj-pill bg-brand-blue-dark px-[11px] py-1 font-display text-jj-label font-bold text-brand-yellow">
              {autorizacoes.pendentes}
              <span className="sr-only"> pendentes</span>
            </span>
          )}
          {prazoFormatado && <span className="ml-auto text-jj-label text-content-soft">Prazo · até {prazoFormatado}</span>}
        </div>

        <div aria-busy={estado === 'carregando'}>
          {estado === 'carregando' && <p className="m-0 text-jj-body-sm text-content-soft">Carregando realizações…</p>}

          {estado === 'erro' && (
            <div className="flex flex-wrap items-center gap-3">
              <p className="m-0 text-jj-body-sm text-content">Não foi possível carregar as realizações.</p>
              <button type="button" onClick={tentarDeNovo} className={BOTAO_DE_TEXTO}>
                Tentar de novo
              </button>
            </div>
          )}

          {estado === 'sem-ciclo' && (
            <p className="m-0 text-jj-body-sm text-content-soft">
              O calendário do ciclo não tem a Apreciação Nível 3, então não há realizações para autorizar.
            </p>
          )}

          {estado === 'pronto' &&
            (autorizacoes.aguardando.length ? (
              <ul className="m-0 flex list-none flex-col gap-3 p-0">{autorizacoes.aguardando.map(cartao)}</ul>
            ) : (
              <p className="m-0 text-jj-body-sm text-content-soft">Nenhuma realização aguardando sua autorização.</p>
            ))}
        </div>
      </section>

      {estado === 'pronto' && autorizacoes.avaliadas.length > 0 && (
        <section aria-labelledby="titulo-avaliadas" className="mt-4 flex flex-col gap-3 rounded-jj-lg bg-brand-white px-6 py-[22px] shadow-jj-01">
          <div className="flex flex-wrap items-center gap-[11px]">
            <h2 id="titulo-avaliadas" className={TITULO_DE_SECAO}>
              Já avaliadas
            </h2>
            <button
              type="button"
              aria-expanded={avaliadasAbertas}
              aria-controls="lista-avaliadas"
              onClick={() => setAvaliadasAbertas(aberta => !aberta)}
              className={`ml-auto ${BOTAO_DE_TEXTO}`}
            >
              {avaliadasAbertas ? 'Ocultar já avaliadas' : `Ver já avaliadas (${autorizacoes.avaliadas.length})`}
            </button>
          </div>
          <ul id="lista-avaliadas" hidden={!avaliadasAbertas} className="m-0 flex list-none flex-col gap-3 p-0">
            {avaliadasAbertas && autorizacoes.avaliadas.map(cartao)}
          </ul>
        </section>
      )}

      <div role="status" className="sr-only">
        {autorizacoes.anuncio}
      </div>
    </div>
  );
}
